<template>
  <section class="assistencia-tecnica">
    <header class="ast-header">
      <div><span class="ast-eyebrow">ASSISTÊNCIA TÉCNICA</span><h1>Bancada de chamados</h1><p>Da entrada do produto até a entrega.</p></div>
      <div class="ast-heading-actions"><span class="ast-tenant">Dados deste tenant · AdminDKP</span>
        <button class="btn btn-primary" :disabled="busy" @click="newTicket">Abrir chamado</button>
      </div>
    </header>
    <nav class="ast-strip" aria-label="Filtrar por etapa">
      <button v-for="s in filters" :key="s.id" :class="{ selected: query.status === s.id }" :aria-pressed="query.status === s.id" @click="setStatus(s.id)">
        <strong>{{ counts[s.id] == null ? '—' : counts[s.id] }}</strong><span>{{ s.nome }}</span>
      </button>
    </nav>
    <div class="ast-filters">
      <input v-model="query.q" class="form-control" type="search" aria-label="Buscar chamados" placeholder="Cliente, produto, telefone ou nº da OS" @input="search">
      <select v-model="query.tecnico" class="form-control" aria-label="Filtrar por técnico" @change="filter"><option value="">Todos os técnicos</option><option v-for="t in tecnicos" :key="t">{{ t }}</option></select>
      <select v-model="query.prioridade" class="form-control" aria-label="Filtrar por prioridade" @change="filter"><option value="">Todas as prioridades</option><option v-for="(label, key) in priorities" :key="key" :value="key">{{ label }}</option></select>
      <select v-model="query.sort" class="form-control" aria-label="Ordenar chamados" @change="filter"><option value="recentes">Atualizados por último</option><option value="antigos">Mais antigos primeiro</option><option value="prioridade">Prioridade</option></select>
    </div>
    <b-alert :show="!!listError" variant="danger">{{ listError }} <button class="btn btn-sm btn-outline-danger" @click="load">Tentar novamente</button></b-alert>
    <b-alert :show="!!success" variant="success" dismissible @dismissed="success = ''">{{ success }}</b-alert>
    <div v-if="loading" class="ast-empty" role="status"><b-spinner small /> Carregando chamados…</div>
    <template v-else-if="!listError">
      <div v-if="!tickets.length" class="ast-empty">
        <feather-icon icon="ToolIcon" size="34" /><h3>{{ counts.todos ? 'Nenhum chamado com esses filtros' : 'Sua bancada está pronta' }}</h3>
        <p>{{ counts.todos ? 'Altere a busca ou os filtros para encontrar um atendimento.' : 'Abra o primeiro chamado para começar a acompanhar seus atendimentos.' }}</p>
        <button v-if="counts.todos" class="btn btn-outline-primary" @click="clearFilters">Limpar filtros</button>
        <button v-else class="btn btn-primary" @click="newTicket">Abrir chamado</button>
      </div>
      <ul v-else class="ast-list">
        <li v-for="t in tickets" :key="t.id">
          <button class="ast-row" :disabled="busy" @click="open(t.id)">
            <span class="ast-number"><small>OS</small><strong>{{ t.numero }}</strong></span>
            <span class="ast-who"><strong>{{ t.cliente }}</strong><span>{{ t.tipo }}{{ t.modelo ? `, ${t.modelo}` : '' }}</span></span>
            <span class="ast-defect">{{ t.defeito }}</span>
            <span class="ast-tech">{{ t.tecnico || 'Sem técnico' }}</span>
            <span class="ast-pills"><span class="ast-pill" :class="`ast-status-${t.status}`">{{ statusName(t.status) }}</span><span v-if="t.prioridade === 'urgente'" class="ast-pill ast-urgent">Urgente</span><span v-if="t.garantia" class="ast-pill">Garantia</span></span>
            <span class="ast-age" :class="{ late: days(t) > 10 && !['pronto', 'entregue'].includes(t.status) }">{{ days(t) }} dia(s)<small>{{ t.status === 'entregue' ? 'até a entrega' : 'na bancada' }}</small></span>
          </button>
        </li>
      </ul>
      <footer class="ast-pagination"><span>{{ meta.total || 0 }} chamado(s) encontrado(s)</span>
        <b-pagination v-if="meta.total > query.pageSize" v-model="query.page" :total-rows="meta.total" :per-page="query.pageSize" first-number last-number aria-label="Página de chamados" @input="load" />
      </footer>
    </template>
    <ticket-form :visible="formVisible" :ticket="editing" :tecnicos="tecnicos" :busy="busy" :error="writeError" :conflict="conflict"
      @close="formVisible = false" @save="save" @reload="reloadEditing" />
    <ticket-detail :visible="detailVisible" :ticket="selected" :busy="busy" :error="writeError" :conflict="conflict"
      @close="detailVisible = false" @patch="patch" @note="note" @delete="remove" @edit="edit" @reload="open(selected.id)" />
  </section>
</template>
<script>
import { BAlert, BSpinner, BPagination } from 'bootstrap-vue';
import client from '@/api/AssistenciaTecnica/chamados.client';
import TicketForm from './TicketForm.vue';
import TicketDetail from './TicketDetail.vue';
import { statuses, priorities, days } from './options';

const initialQuery = () => ({ status: 'ativos', q: '', tecnico: '', prioridade: '', sort: 'recentes', page: 1, pageSize: 20 });
export default {
  components: { BAlert, BSpinner, BPagination, TicketForm, TicketDetail },
  data: () => ({
    query: initialQuery(), tickets: [], counts: {}, tecnicos: [], meta: {}, loading: false,
    busy: false, listError: '', writeError: '', success: '', conflict: false,
    selected: null, editing: null, formVisible: false, detailVisible: false,
    loadToken: 0, epoch: 0, timer: null, priorities,
    filters: [{ id: 'ativos', nome: 'Em andamento' }, ...statuses, { id: 'todos', nome: 'Todos' }],
  }),
  watch: {
    '$route.params.tenantSlug'() {
      this.epoch += 1; this.busy = false; this.selected = null; this.editing = null;
      this.formVisible = false; this.detailVisible = false; this.success = ''; this.writeError = '';
      this.tickets = []; this.counts = {}; this.tecnicos = []; this.clearFilters();
    },
  },
  mounted() { this.load(); },
  beforeDestroy() { this.epoch += 1; this.loadToken += 1; clearTimeout(this.timer); },
  methods: {
    days,
    statusName(id) { return (statuses.find(s => s.id === id) || {}).nome || id; },
    message(e) {
      if (e.response && e.response.status === 409) return 'Este chamado foi alterado por outra pessoa. Recarregue-o antes de salvar novamente.';
      return (e.response && (e.response.data.message || e.response.data.error || e.response.data.msg)) || 'Não foi possível concluir a operação. Verifique sua conexão e tente novamente.';
    },
    async load() {
      clearTimeout(this.timer);
      const token = ++this.loadToken; this.loading = true; this.listError = '';
      try {
        const result = await client.list(this.query);
        if (token !== this.loadToken) return;
        this.tickets = result.data; this.meta = result.meta; this.counts = result.counts; this.tecnicos = result.tecnicos;
        if (!result.data.length && this.query.page > 1) { this.query.page -= 1; this.load(); }
      } catch (e) { if (token === this.loadToken) { this.listError = this.message(e); this.tickets = []; } }
      finally { if (token === this.loadToken) this.loading = false; }
    },
    filter() { this.query.page = 1; this.load(); },
    search() { clearTimeout(this.timer); this.timer = setTimeout(() => this.filter(), 300); },
    setStatus(status) { this.query.status = status; this.filter(); },
    clearFilters() { this.query = initialQuery(); this.query.status = 'todos'; this.load(); },
    newTicket() { this.editing = null; this.writeError = ''; this.conflict = false; this.formVisible = true; },
    async open(id) {
      const epoch = this.epoch; this.busy = true; this.writeError = ''; this.conflict = false;
      try { const t = await client.get(id); if (epoch === this.epoch) { this.selected = t; this.detailVisible = true; } }
      catch (e) { if (epoch === this.epoch) { this.writeError = this.message(e); if (!this.detailVisible) this.listError = this.writeError; } }
      finally { if (epoch === this.epoch) this.busy = false; }
    },
    edit() { this.editing = this.selected; this.detailVisible = false; this.writeError = ''; this.formVisible = true; },
    async reloadEditing() {
      const epoch = this.epoch; this.busy = true;
      try {
        const ticket = await client.get(this.editing.id);
        if (epoch === this.epoch) { this.editing = ticket; this.selected = ticket; this.writeError = ''; this.conflict = false; }
      } catch (e) { if (epoch === this.epoch) this.writeError = this.message(e); }
      finally { if (epoch === this.epoch) this.busy = false; }
    },
    async write(operation, onSuccess) {
      if (this.busy) return false;
      const epoch = this.epoch; this.busy = true; this.writeError = ''; this.conflict = false;
      try {
        const result = await operation();
        if (epoch !== this.epoch) return false;
        onSuccess(result); this.success = 'Alteração salva.'; await this.load(); return true;
      } catch (e) {
        if (epoch === this.epoch) { this.writeError = this.message(e); this.conflict = !!(e.response && e.response.status === 409); }
        return false;
      } finally { if (epoch === this.epoch) this.busy = false; }
    },
    save(data) {
      return this.write(() => (this.editing ? client.update(this.editing.id, data) : client.create(data)),
        t => { this.formVisible = false; this.selected = t; this.detailVisible = true; });
    },
    patch(data) { return this.write(() => client.update(this.selected.id, data), t => { this.selected = t; }); },
    async note(texto, done) {
      if (await this.write(() => client.note(this.selected.id, { texto, version: this.selected.version }), t => { this.selected = t; })) done();
    },
    async remove() {
      const epoch = this.epoch; const { id, version, numero } = this.selected;
      const confirmed = await this.$bvModal.msgBoxConfirm(`Excluir a OS ${numero}? O chamado e seu histórico serão removidos.`, { title: 'Excluir chamado', okTitle: 'Excluir', cancelTitle: 'Cancelar', okVariant: 'danger', centered: true });
      if (confirmed && epoch === this.epoch) this.write(() => client.remove(id, version), () => { this.detailVisible = false; this.selected = null; });
    },
  },
};
</script>
<style src="./assistencia.css"></style>